import React, { FC, useCallback, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import FormDots from '../common/FormDots';
import { Button, Col, Input, InputNumber, Row } from 'antd';
import { Form } from '@ant-design/compatible';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
	addItem,
	getFixedFloatSum,
	LINE_ITEM_FIELD_TO_DISPLAY_NAMES,
	removeItem,
} from '../../utils/InvoiceUtils';

const SimpleLineItemFormFields: FC<any> = ({
	fieldName,
	formData,
	form,
	currency,
	initialUuid,
	state,
	setState,
	recalculateTotals,
}): React.ReactElement => {
	const [, setUuid] = useState(initialUuid);

	const { getFieldDecorator, getFieldValue, setFieldsValue } = form;

	const keyName = `${fieldName}Keys`;

	const keys = getFieldValue(keyName) || [];

	const handleChange = useCallback(
		(val, k) => {
			const charge = val;
			if (charge) {
				setFieldsValue({ [`${fieldName}-${k}-amount`]: charge.toString() });
			} else {
				setFieldsValue({ [`${fieldName}-${k}-amount`]: undefined });
			}
			const totalBeforeTax = keys.reduce((acc, k2) => {
				const price = k2 === k ? charge : getFieldValue(`${fieldName}-${k2}-amount`) || '0';
				return getFixedFloatSum(acc, price);
			}, 0);

			setFieldsValue({ [`${fieldName}TotalBeforeTax`]: totalBeforeTax });
			recalculateTotals();
		},
		[fieldName, getFieldValue, keys, recalculateTotals, setFieldsValue]
	);

	const formItems = useMemo(
		() =>
			keys.map((k, index) => {
				getFieldDecorator(`${fieldName}-${k}-id`, {
					initialValue: nullSafeGet(`${fieldName}-${k}-id`, formData),
				});
				return (
					<Row key={`${fieldName}-line-item-${k}`}>
						<Col span={1}>
							<div
								style={
									index === 0
										? {
												height: 40,
												marginTop: 29,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
										  }
										: {
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
										  }
								}
							>
								{index + 1}
							</div>
						</Col>

						<Col span={12}>
							<Form.Item label={index === 0 ? 'Description' : ''}>
								{getFieldDecorator(`${fieldName}-${k}-description`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`${fieldName}-${k}-description`, formData) ||
										nullSafeGet(
											`${fieldName}ItemInitialValues.${fieldName}-${k}-description`,
											state
										),
									rules: [
										{
											required: true,
											whitespace: true,
											message: 'Please add description.',
										},
									],
								})(<Input />)}
							</Form.Item>
						</Col>
						<Col offset={1} span={4}>
							<Form.Item label={index === 0 ? 'Price' : ''}>
								{getFieldDecorator(`${fieldName}-${k}-unitPrice`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`${fieldName}-${k}-amount`, formData) ||
										nullSafeGet(`${fieldName}ItemInitialValues.${fieldName}-${k}-amount`, state),
									rules: [
										{
											required: true,
											message: 'Please add price.',
										},
									],
								})(
									<InputNumber
										step={0.01}
										onChange={(e) => handleChange(e, k)}
										style={{ width: '100%' }}
										addonAfter={currency.id}
									/>
								)}
							</Form.Item>
						</Col>
						<Col
							span={1}
							style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
						>
							<div
								style={
									index === 0
										? {
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												marginTop: 30,
										  }
										: {
												height: 40,
												display: 'flex',
												alignItems: 'center',
										  }
								}
							>
								{keys.length > 0 ? (
									<MinusCircleOutlined
										className="dynamic-delete-button"
										onClick={() => removeItem(form, recalculateTotals)(keyName, fieldName, k)}
									/>
								) : null}
							</div>
						</Col>

						<Col span={2} offset={3}>
							<div
								style={
									index === 0
										? {
												height: 40,
												marginTop: 29,
												display: 'flex',
												justifyContent: 'flex-end',
												alignItems: 'center',
										  }
										: {
												height: 40,
												display: 'flex',
												justifyContent: 'flex-end',
												alignItems: 'center',
										  }
								}
							>
								{getFieldDecorator(`${fieldName}-${k}-amount`, {
									initialValue:
										formData[`${fieldName}-${k}-amount`] ||
										nullSafeGet(`${fieldName}ItemInitialValues.${fieldName}-${k}-amount`, state),
								})}
								{currency.format(getFieldValue(`${fieldName}-${k}-amount`))}
							</div>
						</Col>
					</Row>
				);
			}),
		[
			currency,
			fieldName,
			form,
			formData,
			getFieldDecorator,
			getFieldValue,
			handleChange,
			keyName,
			keys,
			recalculateTotals,
			state,
		]
	);

	const onAddItem = useCallback(() => {
		setUuid((uuid) => addItem(form, state, setState)(keyName, uuid, `${fieldName}TotalBeforeTax`));
	}, [fieldName, form, keyName, setState, state]);

	return (
		<div style={{ marginBottom: 32 }}>
			<div className="buyerInvoiceForm__sectionHead">
				<div className="buyerInvoiceForm__sectionHeadPrimary">
					{LINE_ITEM_FIELD_TO_DISPLAY_NAMES[fieldName]}
				</div>
				<FormDots />
				{nullSafeGetOrElse('length', keys, 0) > 0 ? (
					getFieldDecorator(`${fieldName}TotalBeforeTax`, {
						validateTrigger: ['onBlur'],
						initialValue: `${nullSafeGetOrElse(`${fieldName}TotalBeforeTax`, formData, 0)}`,
					})(
						<div className="buyerInvoiceForm__sectionHeadSecondary">
							{currency.format(getFieldValue(`${fieldName}TotalBeforeTax`))}
						</div>
					)
				) : (
					<div className="buyerInvoiceForm__sectionHeadSecondary">
						{getFieldDecorator(`${fieldName}TotalBeforeTax`, {
							validateTrigger: ['onBlur'],
							initialValue: `${nullSafeGetOrElse(`${fieldName}TotalBeforeTax`, formData, 0)}`,
						})(
							<InputNumber
								step={1}
								onChange={recalculateTotals}
								style={{ width: 240 }}
								addonAfter={currency.id}
							/>
						)}
					</div>
				)}
			</div>
			{formItems}
			<Row>
				<Col span={16} offset={1}>
					<Button type="dashed" onClick={onAddItem} style={{ height: 32 }}>
						<PlusOutlined />
						{` Add ${LINE_ITEM_FIELD_TO_DISPLAY_NAMES[fieldName]}`}
					</Button>
				</Col>
			</Row>
		</div>
	);
};

export default SimpleLineItemFormFields;
